<template>
  <div class="favorites-page">
    <div class="container">
      <div v-if="isLoading" class="loading-state">
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12" :md="8" :lg="6" v-for="n in 8" :key="`skel-${n}`" class="skeleton-col">
            <el-skeleton style="width: 100%" animated>
              <template #template>
                <el-skeleton-item variant="image" style="width: 100%; height: 200px;" />
                <div style="padding: 14px;">
                  <el-skeleton-item variant="p" style="width: 70%" />
                  <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 10px;">
                    <el-skeleton-item variant="text" style="margin-right: 16px; width: 30%;" />
                    <el-skeleton-item variant="button" style="width: 80px; height: 32px;" />
                  </div>
                </div>
              </template>
            </el-skeleton>
          </el-col>
        </el-row>
      </div>
 
      <div v-else-if="favoriteItems.length === 0" class="empty-state">
        <el-empty description="您還沒有收藏任何商品哦～">
          <el-button type="primary" @click="goToShopping">馬上去逛逛</el-button>
        </el-empty>
      </div>

      <div v-else class="favorites-grid">
        <div v-for="item in favoriteItems" :key="item.id" class="favorite-card">
          <div class="card-image-wrapper" @click="goToProduct(item.productId)">
            <img
              :src="`${backendBaseUrl}/${item.imageUrl}`"
              :alt="item.name"
              class="card-img"
              @error="handleImageError"
              loading="lazy"
            />
          </div>
          <div class="card-content">
            <h3 class="card-name" :title="item.name" @click="goToProduct(item.productId)">
              {{ item.name }}
            </h3>
            <p v-if="item.price" class="card-price">${{ item.price.toLocaleString() }}</p>
            <div class="card-actions">
              <el-button
                type="danger"
                plain
                size="small"
                @click="unfavoriteItem(item.id, item.productId)"
                class="unfavorite-btn"
              >
                <el-icon><Delete /></el-icon> 取消收藏
              </el-button>
            </div>
          </div>
        </div>
      </div>
      <!-- 如果需要分页，可以在这里添加 Pagination 组件 -->
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Delete } from '@element-plus/icons-vue';
import CollService from '../../services/CollectManage';

const router = useRouter();
const isLoading = ref(true);
const favoriteItems = ref([]); // { id (收藏记录ID), productId, name, imageUrl, price }
const backendBaseUrl = process.env.VUE_APP_BACKEND_BASE_URL;

// 模拟从 API 获取收藏数据
const fetchFavoriteItems = async () => {
  isLoading.value = true;
  try {
    const response = await CollService.getColl();
    favoriteItems.value = response;
  } catch (error) {
    ElMessage.error('加载收藏列表失败，请稍后重试');
    console.error("Error fetching favorite items:", error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchFavoriteItems();
});

const unfavoriteItem = async (favoriteId, productId) => {
  try {
    await ElMessageBox.confirm('确定要取消收藏该商品吗？', '提示', {
      confirmButtonText: '确定取消',
      cancelButtonText: '再想想',
      type: 'warning',
    });
    await CollService.deleteColl(favoriteId);
    ElMessage.success('已取消收藏');
    favoriteItems.value = favoriteItems.value.filter(item => item.id !== favoriteId);
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('取消收藏失败');
      console.error("Error unfavoriting item:", error);
    }
  }
};

const handleImageError = (event) => {
  event.target.src = 'https://via.placeholder.com/300x200/E0E0E0/B0B0B0?text=Image+Error';
  event.target.alt = '图片加载失败';
};

const goToShopping = () => {
  router.push('/shoes'); // 或者您想跳转的主商品列表页
};

const goToProduct = (productId) => {
  router.push({ name: 'ShoeDetail', params: { id: productId } });
};

</script>

<style scoped lang="scss">
.favorites-page {
  min-height: calc(100vh - 72px); /* 假设头部高度为72px */
  padding: 2.5rem 0; /* 上下留白 */
}

.container {
  max-width: 1280px; /* 调整内容最大宽度 */
  margin: 0 auto;
  padding: 0 1rem; /* 左右内边距 */
}

.loading-state {
  padding: 2rem 0;
}
.skeleton-col {
    margin-bottom: 20px; /* 骨架屏列之间的底部间距 */
}


.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 350px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03);
  padding: 2.5rem;
  margin-top: 1rem;

  .el-button {
    margin-top: 2rem;
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
}

.favorites-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); /* 响应式网格 */
  gap: 1.75rem; /* 卡片间距 */
}

.favorite-card {
  background-color: #fff;
  border-radius: 10px; /* 更圆润的卡片 */
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.07), 0 1px 3px rgba(0,0,0,0.05); /* 柔和的阴影 */
  transition: transform 0.25s ease-out, box-shadow 0.25s ease-out;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1), 0 3px 6px rgba(0,0,0,0.08);
  }
}

.card-image-wrapper {
  position: relative;
  width: 100%;
  padding-top: 75%; /* 图片比例 4:3，您可以调整为 100% (1:1) 或 56.25% (16:9) */
  background-color: #eef2f7; /* 图片加载时的占位背景色 */
  cursor: pointer;
  overflow: hidden; /* 确保图片圆角（如果图片本身有）和hover效果 */
}

.card-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}
.favorite-card:hover .card-img {
    transform: scale(1.05); /* 图片轻微放大 */
}

.card-content {
  padding: 1rem 1.25rem 1.25rem; /* 内边距 */
  display: flex;
  flex-direction: column;
  flex-grow: 1; /* 使内容区域填满卡片剩余空间 */
  text-align: left;
}

.card-name {
  font-size: 1.05rem; /* 17px */
  font-weight: 600;
  color: #2d3748; /* 较深的灰色 */
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
  cursor: pointer;
  // 最多显示两行，超出部分用省略号
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: calc(1.05rem * 1.4 * 2); /* 确保两行的高度，避免跳动 */
}

.card-price {
  font-size: 1.1rem; /* 价格字体稍大 */
  font-weight: 700;
  color: #e53e3e; /* 醒目的红色价格 */
  margin-bottom: 0.75rem;
}

.card-actions {
  margin-top: auto; /* 将按钮推到卡片底部 */
  padding-top: 0.75rem; /* 与上方内容的一些间距 */
  display: flex;
  justify-content: flex-end; /* 按钮靠右 */
}

.unfavorite-btn {
  // el-button 的 size="small" 已经比较合适了
  // 如果需要自定义，可以覆盖：
  // padding: 0.4rem 0.8rem;
  // font-size: 0.8rem;
  .el-icon {
    margin-right: 4px;
  }
}

// Responsive adjustments
@media (max-width: 1024px) { // 中等屏幕
  .favorites-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1.5rem;
  }
}

@media (max-width: 768px) { // 平板和小屏幕
  .favorites-page {
      padding: 2rem 0;
  }
  .container {
      padding: 0 0.75rem;
  }
  .favorites-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); // 可以调整为更小的 minmax
    gap: 1rem;
  }
  .card-name {
    font-size: 0.95rem;
    min-height: calc(0.95rem * 1.4 * 2);
  }
  .card-price {
      font-size: 1rem;
  }
}

@media (max-width: 480px) { // 手机
  .favorites-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); // 在非常小的屏幕上，一行两个或一个
    // 如果想强制一行一个：grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  .favorite-card {
      border-radius: 8px;
  }
  .card-content {
    padding: 0.75rem 1rem 1rem;
  }
  .card-name {
    font-size: 0.875rem; /* 14px */
    min-height: calc(0.875rem * 1.4 * 2);
  }
  .card-price {
      font-size: 0.95rem;
      margin-bottom: 0.5rem;
  }
  .unfavorite-btn {
      width: 100%; // 在小屏幕上，取消收藏按钮可以占满宽度
  }
}
</style>